import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { Loader2, Wallet, TrendingUp, Coins, Receipt, CalendarClock, Download, ChevronRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatDateTime } from "@/lib/statuts";

const PERIODS = [
  { k: "today", l: "Aujourd'hui" },
  { k: "week", l: "Cette semaine" },
  { k: "month", l: "Ce mois" },
  { k: "year", l: "Cette année" },
];

export default function Comptabilite() {
  const [tab, setTab] = useState("prepaiement");
  const [period, setPeriod] = useState("month");
  const [inst, setInst] = useState("");
  const [ents, setEnts] = useState([]);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState("");

  useEffect(() => { api.get("/entreprises").then((r) => setEnts(r.data.filter((e) => e.entite !== "NOVASOL"))).catch(() => {}); }, []);

  useEffect(() => {
    setData(null);
    const p = new URLSearchParams({ period });
    if (inst) p.set("installateur_id", inst);
    api.get(`/comptabilite?${p}`).then((r) => setData(r.data)).catch((e) => setError(apiError(e)));
  }, [period, inst]);

  const doExport = async (fmt) => {
    setExporting(fmt);
    try {
      const p = new URLSearchParams({ tab, format: fmt, period });
      if (inst) p.set("installateur_id", inst);
      const res = await api.get(`/comptabilite/export?${p}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const ext = fmt === "xlsx" ? "xlsx" : fmt;
      a.download = `comptabilite_${tab}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export impossible"); } finally { setExporting(""); }
  };

  if (error) return <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-rose-700" data-testid="compta-error">{error}</div>;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">Comptabilité</h1>
          <p className="text-sm text-slate-500">Suivi financier — prépaiement (jetons) et facturation classique</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="compta-partner" value={inst} onChange={(e) => setInst(e.target.value)} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs">
            <option value="">Tous les partenaires</option>
            {ents.map((e) => <option key={e.id} value={e.id}>{e.raison_sociale}</option>)}
          </select>
          <div className="flex rounded-lg border border-slate-200 bg-white p-1">
            {PERIODS.map((p) => (
              <button key={p.k} data-testid={`compta-period-${p.k}`} onClick={() => setPeriod(p.k)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${period === p.k ? "bg-[#0A0A0A] text-white" : "text-slate-500 hover:text-slate-900"}`}>
                {p.l}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1">
            <span className="px-1 text-[11px] font-semibold text-slate-400"><Download className="inline h-3.5 w-3.5" /></span>
            {["csv", "xlsx", "pdf"].map((f) => (
              <button key={f} data-testid={`compta-export-${f}`} onClick={() => doExport(f)} disabled={!!exporting}
                className="rounded-md px-2 py-1 text-[11px] font-bold uppercase text-slate-600 hover:bg-slate-100 disabled:opacity-50">
                {exporting === f ? <Loader2 className="h-3 w-3 animate-spin" /> : (f === "xlsx" ? "Excel" : f)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        <TabBtn active={tab === "prepaiement"} onClick={() => setTab("prepaiement")} testid="tab-prepaiement" icon={Coins} label="Prépaiement (jetons)" />
        <TabBtn active={tab === "hors"} onClick={() => setTab("hors")} testid="tab-hors" icon={Receipt} label="Hors-prépaiement" />
      </div>

      {!data ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : tab === "prepaiement" ? (
        <Prepaiement d={data.prepaiement} />
      ) : (
        <HorsPrepaiement d={data.hors_prepaiement} />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, testid, icon: Icon, label }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors duration-200 ${active ? "border-[#0A0A0A] text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

function SummaryCard({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
        <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ backgroundColor: `${color}1a` }}><Icon className="h-4 w-4" style={{ color }} /></div>
      </div>
      <div className="mt-2 font-heading text-2xl font-black text-slate-900">{value}</div>
    </div>
  );
}

function Prepaiement({ d }) {
  const [open, setOpen] = useState({});
  return (
    <div className="space-y-4" data-testid="compta-prepaiement">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <SummaryCard icon={Wallet} label="Total achats jetons" value={`${d.total_achats.toLocaleString("fr-FR")} €`} color="#8b5cf6" />
        <SummaryCard icon={TrendingUp} label="Valeur consommée" value={`${d.total_consomme_valeur.toLocaleString("fr-FR")} €`} color="#10b981" />
        <SummaryCard icon={Coins} label="Partenaires prépaiement" value={d.partenaires.length} color="#3b82f6" />
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm" data-testid="prepaiement-table">
            <thead><tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <th className="px-4 py-2.5">Partenaire</th><th className="px-4 py-2.5">Achetés</th><th className="px-4 py-2.5">Consommés</th><th className="px-4 py-2.5">Restants</th><th className="px-4 py-2.5">Achats (€)</th><th className="px-4 py-2.5">Valeur consommée</th>
            </tr></thead>
            <tbody>
              {d.partenaires.length === 0 ? (
                <tr><td colSpan={6} className="py-12 text-center text-slate-400">Aucun partenaire en prépaiement</td></tr>
              ) : d.partenaires.map((p) => (
                <React.Fragment key={p.entreprise_id}>
                  <tr data-testid={`prepaiement-row-${p.entreprise_id}`} onClick={() => setOpen((o) => ({ ...o, [p.entreprise_id]: !o[p.entreprise_id] }))} className="cursor-pointer border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="px-4 py-3 font-medium text-slate-900">
                      <span className="flex items-center gap-1.5">
                        {open[p.entreprise_id] ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                        {p.nom}
                      </span>
                    </td>
                    <td className="px-4 py-3 mono">{p.achats_qte}</td>
                    <td className="px-4 py-3 mono text-amber-600">{p.consommes_qte}</td>
                    <td className="px-4 py-3 mono font-semibold text-violet-700">{p.restant_qte}</td>
                    <td className="px-4 py-3 mono font-semibold text-slate-900">{p.achats_montant.toLocaleString("fr-FR")} €</td>
                    <td className="px-4 py-3 mono text-emerald-600">{p.valeur_consommee.toLocaleString("fr-FR")} €</td>
                  </tr>
                  {open[p.entreprise_id] && (
                    <tr><td colSpan={6} className="bg-slate-50/70 px-4 py-3" data-testid={`recharges-${p.entreprise_id}`}>
                      <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">Historique des recharges (achats de jetons)</div>
                      {p.recharges.length === 0 ? <div className="text-xs text-slate-400">Aucune recharge sur la période</div> : (
                        <table className="w-full text-left text-xs">
                          <thead><tr className="text-slate-400"><th className="py-1">Date</th><th className="py-1">Type VT</th><th className="py-1">Quantité</th><th className="py-1">Montant</th><th className="py-1">Référence</th></tr></thead>
                          <tbody>
                            {p.recharges.map((r, i) => (
                              <tr key={i} className="border-t border-slate-200">
                                <td className="py-1.5">{formatDateTime(r.date)}</td>
                                <td className="py-1.5">{r.vt_type}</td>
                                <td className="py-1.5 mono text-emerald-600">+{r.quantite}</td>
                                <td className="py-1.5 mono">{r.montant.toLocaleString("fr-FR")} €</td>
                                <td className="py-1.5 text-slate-400">{r.reference}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function HorsPrepaiement({ d }) {
  const [open, setOpen] = useState({});
  return (
    <div className="space-y-4" data-testid="compta-hors">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <SummaryCard icon={CalendarClock} label="Visites sur la période" value={d.total_visites} color="#3b82f6" />
        <SummaryCard icon={Wallet} label="Montant facturable" value={`${d.total_montant.toLocaleString("fr-FR")} €`} color="#10b981" />
        <SummaryCard icon={Receipt} label="Partenaires facturés" value={d.partenaires.length} color="#f59e0b" />
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-left text-sm" data-testid="hors-table">
            <thead><tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <th className="px-4 py-2.5">Partenaire</th><th className="px-4 py-2.5">Visites</th><th className="px-4 py-2.5">Terminées</th><th className="px-4 py-2.5">Montant total</th>
            </tr></thead>
            <tbody>
              {d.partenaires.length === 0 ? (
                <tr><td colSpan={4} className="py-12 text-center text-slate-400">Aucune visite facturable sur cette période</td></tr>
              ) : d.partenaires.map((p) => (
                <React.Fragment key={p.entreprise_id}>
                  <tr data-testid={`hors-row-${p.entreprise_id}`} onClick={() => setOpen((o) => ({ ...o, [p.entreprise_id]: !o[p.entreprise_id] }))} className="cursor-pointer border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="px-4 py-3 font-medium text-slate-900">
                      <span className="flex items-center gap-1.5">
                        {open[p.entreprise_id] ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                        {p.nom}
                      </span>
                    </td>
                    <td className="px-4 py-3 mono">{p.nb_visites}</td>
                    <td className="px-4 py-3 mono text-emerald-600">{p.terminees}</td>
                    <td className="px-4 py-3 mono font-semibold text-slate-900">{p.montant_total.toLocaleString("fr-FR")} €</td>
                  </tr>
                  {open[p.entreprise_id] && (
                    <tr><td colSpan={4} className="bg-slate-50/70 px-4 py-3" data-testid={`visites-${p.entreprise_id}`}>
                      <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">Rendez-vous de la période</div>
                      {p.visites.length === 0 ? <div className="text-xs text-slate-400">Aucun rendez-vous</div> : (
                        <table className="w-full text-left text-xs">
                          <thead><tr className="text-slate-400"><th className="py-1">N° dossier</th><th className="py-1">Date</th><th className="py-1">Client</th><th className="py-1">Type VT</th><th className="py-1">Statut</th><th className="py-1">Prix</th></tr></thead>
                          <tbody>
                            {p.visites.map((v, i) => (
                              <tr key={i} className="border-t border-slate-200">
                                <td className="py-1.5 mono text-amber-600">{v.numero_dossier}</td>
                                <td className="py-1.5">{v.date_visite ? formatDate(v.date_visite) : "—"}</td>
                                <td className="py-1.5">{v.client_nom}</td>
                                <td className="py-1.5">{v.type_visite}</td>
                                <td className="py-1.5 text-slate-500">{v.statut}</td>
                                <td className="py-1.5 mono">{(v.prix || 0).toLocaleString("fr-FR")} €</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
